import React, { useMemo } from 'react';
import { APITypes, MedicalDataTypes, Dynamo } from '../../../../primary-record-types';
import { getIntl } from '../../../../utils/intl';
import { FastField } from 'formik';
import { getDateFormatValidator, required } from '../../../../utils/validators';
import ImagesField, { formValueImagesToResourceImageUpdates, resourceImagesToFormValueImages } from '../ImagesField';
import { FormValueImage } from '../ImagesField/types';
import { handleFormikSubmit } from '../../../../utils/handleFormikSubmit';
import ProviderFields, { getProviderPropsInitialValues, getProviderSubmissionValues } from '../ProviderFields';
import useRecordEvent from '../../../../hooks/useRecordEvent';
import { CenteredFormRow, Form, SplitFormRow } from '../../../v2BaseComponents/Form';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { FormStack } from '../../../v2BaseComponents/Stack';

const intl = getIntl('ProceduresForm', {
    save: 'Save',
    procedure: 'Procedure',
    notes: 'Additional Information',
    dateTime: 'Date and Time',
    procedureType: 'Procedure Type',
    address1: 'Address 1',
    address2: 'Address 2',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    selectCareTeamProvider: 'Select Care Team Provider',
    provider: 'Provider',
    providerName: 'Provider Name',
});

export type ProcedureFormOwnProps = {
    isLoading: boolean;
    startDateTime?: string;
    defaultResource?: MedicalDataTypes.PrimaryRecordProcedure;
    defaultImages?: Dynamo.ResourceImage[];
    onSubmit: (data: APITypes.medicalDataService.UpdateResourceBody<MedicalDataTypes.PrimaryRecordProcedure>) => void;
    isDisabled: boolean;
    profileId: string;
    resourceId?: string;
    providers: APITypes.medicalProfileService.ProviderCareTeamMemberDetails<APITypes.medicalProfileService.ProviderCareTeamContactDetails>[];
    onCancel: () => void;
};

type ProcedureResourceFormValue = MedicalDataTypes.PrimaryRecordProcedure & {
    selectExistingProvider: boolean;
};

type FormValue = {
    resource: ProcedureResourceFormValue;
    images: FormValueImage[];
};

const getDefaultFormValue = (selectExistingProvider: boolean, startDateTime?: string): FormValue => ({
    resource: {
        id: '', //ignored
        version: '1',
        type: 'procedure',
        name: '',
        dateTime: startDateTime || null,
        notes: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        selectExistingProvider,
        providerCareTeamMemberId: null,
        providerName: null,
        results: '',
    },
    images: [],
});

export type ProcedureFormProps = ProcedureFormOwnProps;

const ProcedureForm: React.FC<ProcedureFormProps> = ({
    isLoading,
    onSubmit,
    isDisabled,
    defaultImages,
    defaultResource,
    startDateTime,
    profileId,
    resourceId,
    providers,
    onCancel,
}) => {
    const initialValues = useMemo<FormValue>(() => {
        return defaultResource && defaultImages
            ? {
                  ...(getProviderPropsInitialValues(
                      providers,
                      {
                          resource: {
                              ...defaultResource,
                              selectExistingProvider: !!defaultResource.providerCareTeamMemberId,
                          },
                      },
                      getDefaultFormValue(!!providers.length, startDateTime),
                  ) as Pick<FormValue, 'resource'>),
                  images: resourceImagesToFormValueImages(defaultImages),
              }
            : getDefaultFormValue(!!providers.length, startDateTime);
    }, [defaultImages, defaultResource, providers, startDateTime]);
    const recordEvent = useRecordEvent();

    return (
        <Form
            actionContainerSubmit
            initialValues={initialValues}
            onSubmit={(values, helpers) => {
                onSubmit({
                    resourceUpdate: {
                        ...getProviderSubmissionValues(values.resource, providers, false),
                    },
                    imageUpdates: formValueImagesToResourceImageUpdates(values.images),
                });
                recordEvent(!!defaultResource ? 'editProcedure' : 'createProcedure');
                handleFormikSubmit(values, helpers);
            }}
            onCancel={onCancel}
            cancelButtonVariant="secondary"
        >
            {({ values, setFieldValue }) => {
                return (
                    <FormStack>
                        <SplitFormRow>
                            <FastField
                                component={FormikInputs.Input}
                                name="resource.name"
                                isRequired
                                title={intl('procedure')}
                                isDisabled={isDisabled || isLoading}
                                validate={required}
                            />
                            <FastField
                                name="resource.dateTime"
                                title={intl('dateTime')}
                                component={FormikInputs.DateTimePicker}
                                validate={getDateFormatValidator('MM/DD/YYYY')}
                                isDisabled={isDisabled || isLoading}
                            />
                        </SplitFormRow>
                        <SplitFormRow>
                            <FastField
                                component={FormikInputs.TextArea}
                                name="resource.notes"
                                title={intl('notes')}
                                isDisabled={isDisabled || isLoading}
                            />
                        </SplitFormRow>
                        <CenteredFormRow>
                            <ImagesField
                                isDisabled={isDisabled || isLoading}
                                values={values.images}
                                resourceId={resourceId}
                                profileId={profileId}
                                category="procedure"
                                setFieldValue={setFieldValue}
                            />
                        </CenteredFormRow>
                        <ProviderFields
                            value={values}
                            providers={providers}
                            isDisabled={isDisabled}
                            isLoading={isLoading}
                            setFieldValue={setFieldValue}
                        />
                    </FormStack>
                );
            }}
        </Form>
    );
};

export default React.memo(ProcedureForm);
